import {
  BoldIcon,
  ItalicIcon,
  LinkIcon,
  ListBulletIcon,
  NumberedListIcon,
  PaintBrushIcon,
  PhotoIcon,
  SparklesIcon,
  UnderlineIcon,
} from '@heroicons/react/24/outline';
import React, { useCallback, useEffect, useRef, useState } from 'react';

interface RichTextEditorProps {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  onAIGenerate?: () => void;
  isAIGenerating?: boolean;
  className?: string;
  minHeight?: string;
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value = '',
  onChange,
  placeholder = 'Start typing...',
  onAIGenerate,
  isAIGenerating = false,
  className = '',
  minHeight = '200px',
}) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const [isToolbarVisible, setIsToolbarVisible] = useState(false);
  const [selectedText, setSelectedText] = useState('');
  const [currentFormat, setCurrentFormat] = useState<Set<string>>(new Set());

  // Initialize editor content
  useEffect(() => {
    if (editorRef.current && value !== editorRef.current.innerHTML) {
      editorRef.current.innerHTML = value;
      // Do NOT dispatch a synthetic input event here. The caller already owns
      // this value, and re-firing onChange creates a feedback loop that falsely
      // flags content as user-modified right after a programmatic update
      // (e.g. AI Generate populating the description).
    }
  }, [value]);

  // Handle content changes
  const handleInput = useCallback(() => {
    if (editorRef.current && onChange) {
      const content = editorRef.current.innerHTML;
      onChange(content);
    }
  }, [onChange]);

  // Update current formatting state
  const updateFormatState = useCallback(() => {
    const formats = new Set<string>();

    if (document.queryCommandState('bold')) formats.add('bold');
    if (document.queryCommandState('italic')) formats.add('italic');
    if (document.queryCommandState('underline')) formats.add('underline');
    if (document.queryCommandState('insertUnorderedList')) formats.add('bullet');
    if (document.queryCommandState('insertOrderedList')) formats.add('numbered');

    setCurrentFormat(formats);
  }, []);

  // Handle selection changes
  const handleSelectionChange = useCallback(() => {
    const selection = window.getSelection();
    if (selection && selection.toString()) {
      setSelectedText(selection.toString());
      setIsToolbarVisible(true);
    } else {
      setSelectedText('');
    }
    updateFormatState();
  }, [updateFormatState]);

  // Apply formatting command
  const executeCommand = useCallback(
    (command: string, value?: string) => {
      document.execCommand(command, false, value);
      editorRef.current?.focus();
      handleInput();
      updateFormatState();
    },
    [handleInput, updateFormatState]
  );

  // Color picker functionality
  const applyColor = useCallback(
    (color: string, background = false) => {
      executeCommand(background ? 'hiliteColor' : 'foreColor', color);
    },
    [executeCommand]
  );

  // Insert link
  const insertLink = useCallback(() => {
    const url = prompt('Enter URL:');
    if (url) {
      executeCommand('createLink', url);
    }
  }, [executeCommand]);

  // Insert image
  const insertImage = useCallback(() => {
    const url = prompt('Enter image URL:');
    if (url) {
      executeCommand('insertImage', url);
    }
  }, [executeCommand]);

  // AI content generation
  const handleAIGenerate = useCallback(() => {
    if (onAIGenerate && !isAIGenerating) {
      onAIGenerate();
      // Focus the editor after AI generation
      setTimeout(() => {
        editorRef.current?.focus();
      }, 100);
    }
  }, [onAIGenerate, isAIGenerating]);

  // Setup event listeners
  useEffect(() => {
    document.addEventListener('selectionchange', handleSelectionChange);
    return () => {
      document.removeEventListener('selectionchange', handleSelectionChange);
    };
  }, [handleSelectionChange]);

  const colorPalette = [
    '#000000',
    '#333333',
    '#666666',
    '#999999',
    '#CCCCCC',
    '#FF0000',
    '#FF6600',
    '#FFCC00',
    '#00FF00',
    '#0066FF',
    '#6600FF',
    '#FF0066',
    '#00FFFF',
    '#FF00FF',
    '#FFFF00',
  ];

  return (
    <div className={`relative rich-editor ${className}`}>
      {/* Modern Toolbar */}
      <div className="bg-white border border-gray-200 rounded-t-lg p-3 flex items-center justify-between shadow-sm">
        <div className="flex items-center space-x-1">
          {/* Basic Formatting */}
          <div className="flex items-center space-x-1 pr-2 border-r border-gray-200">
            <button
              type="button"
              onClick={() => executeCommand('bold')}
              className={`p-2 rounded-md transition-colors ${
                currentFormat.has('bold')
                  ? 'bg-blue-100 text-blue-700'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
              title="Bold (Ctrl+B)"
            >
              <BoldIcon className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => executeCommand('italic')}
              className={`p-2 rounded-md transition-colors ${
                currentFormat.has('italic')
                  ? 'bg-blue-100 text-blue-700'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
              title="Italic (Ctrl+I)"
            >
              <ItalicIcon className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => executeCommand('underline')}
              className={`p-2 rounded-md transition-colors ${
                currentFormat.has('underline')
                  ? 'bg-blue-100 text-blue-700'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
              title="Underline (Ctrl+U)"
            >
              <UnderlineIcon className="h-4 w-4" />
            </button>
          </div>

          {/* Lists */}
          <div className="flex items-center space-x-1 px-2 border-r border-gray-200">
            <button
              type="button"
              onClick={() => executeCommand('insertUnorderedList')}
              className={`p-2 rounded-md transition-colors ${
                currentFormat.has('bullet')
                  ? 'bg-blue-100 text-blue-700'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
              title="Bullet List"
            >
              <ListBulletIcon className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => executeCommand('insertOrderedList')}
              className={`p-2 rounded-md transition-colors ${
                currentFormat.has('numbered')
                  ? 'bg-blue-100 text-blue-700'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
              title="Numbered List"
            >
              <NumberedListIcon className="h-4 w-4" />
            </button>
          </div>

          {/* Text Alignment */}
          <div className="flex items-center space-x-1 px-2 border-r border-gray-200">
            <button
              type="button"
              onClick={() => executeCommand('justifyLeft')}
              className="p-2 rounded-md text-gray-600 hover:bg-gray-100 transition-colors"
              title="Align Left"
            >
              <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                <path d="M2 4.5A.5.5 0 0 1 2.5 4h15a.5.5 0 0 1 0 1h-15a.5.5 0 0 1-.5-.5zm0 2A.5.5 0 0 1 2.5 6h11a.5.5 0 0 1 0 1h-11a.5.5 0 0 1-.5-.5zm0 2A.5.5 0 0 1 2.5 8h15a.5.5 0 0 1 0 1h-15a.5.5 0 0 1-.5-.5zm0 2A.5.5 0 0 1 2.5 10h9a.5.5 0 0 1 0 1h-9a.5.5 0 0 1-.5-.5z" />
              </svg>
            </button>
            <button
              type="button"
              onClick={() => executeCommand('justifyCenter')}
              className="p-2 rounded-md text-gray-600 hover:bg-gray-100 transition-colors"
              title="Center"
            >
              <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                <path d="M2 4.5A.5.5 0 0 1 2.5 4h15a.5.5 0 0 1 0 1h-15a.5.5 0 0 1-.5-.5zm2 2A.5.5 0 0 1 4.5 6h11a.5.5 0 0 1 0 1h-11a.5.5 0 0 1-.5-.5zm-2 2A.5.5 0 0 1 2.5 8h15a.5.5 0 0 1 0 1h-15a.5.5 0 0 1-.5-.5zm2 2A.5.5 0 0 1 4.5 10h9a.5.5 0 0 1 0 1h-9a.5.5 0 0 1-.5-.5z" />
              </svg>
            </button>
            <button
              type="button"
              onClick={() => executeCommand('justifyRight')}
              className="p-2 rounded-md text-gray-600 hover:bg-gray-100 transition-colors"
              title="Align Right"
            >
              <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                <path d="M2 4.5A.5.5 0 0 1 2.5 4h15a.5.5 0 0 1 0 1h-15a.5.5 0 0 1-.5-.5zm4 2A.5.5 0 0 1 6.5 6h11a.5.5 0 0 1 0 1h-11a.5.5 0 0 1-.5-.5zm-4 2A.5.5 0 0 1 2.5 8h15a.5.5 0 0 1 0 1h-15a.5.5 0 0 1-.5-.5zm6 2A.5.5 0 0 1 8.5 10h9a.5.5 0 0 1 0 1h-9a.5.5 0 0 1-.5-.5z" />
              </svg>
            </button>
          </div>

          {/* Colors */}
          <div className="relative group px-2 border-r border-gray-200">
            <button
              type="button"
              className="p-2 rounded-md text-gray-600 hover:bg-gray-100 transition-colors"
              title="Text Color"
            >
              <PaintBrushIcon className="h-4 w-4" />
            </button>
            <div className="absolute top-full left-0 mt-1 p-2 bg-white border border-gray-200 rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity z-10">
              <div className="grid grid-cols-5 gap-1 w-32">
                {colorPalette.map((color) => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => applyColor(color)}
                    className="w-6 h-6 rounded border border-gray-300 hover:scale-110 transition-transform"
                    style={{ backgroundColor: color }}
                    title={color}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Media & Links */}
          <div className="flex items-center space-x-1 px-2">
            <button
              type="button"
              onClick={insertLink}
              className="p-2 rounded-md text-gray-600 hover:bg-gray-100 transition-colors"
              title="Insert Link"
            >
              <LinkIcon className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={insertImage}
              className="p-2 rounded-md text-gray-600 hover:bg-gray-100 transition-colors"
              title="Insert Image"
            >
              <PhotoIcon className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* AI Generate Button */}
        {onAIGenerate && (
          <button
            type="button"
            onClick={handleAIGenerate}
            disabled={isAIGenerating}
            className={`flex items-center px-3 py-1.5 bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-md transition-colors text-sm ${
              isAIGenerating
                ? 'opacity-60 cursor-not-allowed'
                : 'hover:from-purple-600 hover:to-blue-600'
            }`}
          >
            {isAIGenerating ? (
              <>
                <svg className="animate-spin h-4 w-4 mr-1" viewBox="0 0 24 24" fill="none">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                  />
                </svg>
                Generating…
              </>
            ) : (
              <>
                <SparklesIcon className="h-4 w-4 mr-1" />
                AI Generate
              </>
            )}
          </button>
        )}
      </div>

      {/* Editor Content */}
      <div
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        onFocus={() => setIsToolbarVisible(true)}
        onBlur={() => setTimeout(() => setIsToolbarVisible(false), 100)}
        className="w-full p-4 border border-t-0 border-gray-200 rounded-b-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        style={{ minHeight }}
        data-placeholder={placeholder}
        suppressContentEditableWarning={true}
      />

      {/* Floating Toolbar for Selected Text */}
      {selectedText && isToolbarVisible && (
        <div className="absolute z-20 bg-gray-900 text-white rounded-lg shadow-xl px-2 py-1 flex items-center space-x-1 transform -translate-y-full">
          <button
            type="button"
            onClick={() => executeCommand('bold')}
            className="p-1 rounded hover:bg-gray-700 transition-colors"
          >
            <BoldIcon className="h-3 w-3" />
          </button>
          <button
            type="button"
            onClick={() => executeCommand('italic')}
            className="p-1 rounded hover:bg-gray-700 transition-colors"
          >
            <ItalicIcon className="h-3 w-3" />
          </button>
          <button
            type="button"
            onClick={() => executeCommand('underline')}
            className="p-1 rounded hover:bg-gray-700 transition-colors"
          >
            <UnderlineIcon className="h-3 w-3" />
          </button>
          <div className="w-px h-4 bg-gray-600"></div>
          <button
            type="button"
            onClick={insertLink}
            className="p-1 rounded hover:bg-gray-700 transition-colors"
          >
            <LinkIcon className="h-3 w-3" />
          </button>
        </div>
      )}

      {/* Placeholder styling */}
      <style
        dangerouslySetInnerHTML={{
          __html: `
          .rich-editor [contenteditable]:empty:before {
            content: attr(data-placeholder);
            color: #9ca3af;
            pointer-events: none;
          }
        `,
        }}
      />
    </div>
  );
};

export default RichTextEditor;
